import { displayParentOf, permissionLabel } from '@agos/manager-hr'
import { tt } from '@agos/shared'

import type { AccessRefusal } from './access'

/**
 * What `RouteGate` says when it refuses a route - the sentences, apart from the markup, so they can be asked
 * without rendering.
 *
 * **§9.759, FB-1078 - a refusal names its other ways in, and a right as *Role a práva* titles it.** The owner's
 * administrator read *Chýba právo: driver* about a right the catalogue never held, and nothing on the screen said
 * the other two ways into the driving app: the role and the mark *Jazdí aj rozvoz*. Two changes, both additive:
 * a role refusal whose guard carries `orAlsoDrives` names the mark beside the roles, and a permission refusal
 * names each right by the title the roles screen gives it, with the key after it as that screen prints the key
 * under every row - so the person who has to tick it finds it by the same words.
 */

function roleName(role: string): string {
  if (role === 'main_admin') return tt('hlavný administrátor')
  if (role === 'admin') return tt('administrátor')
  if (role === 'cook') return tt('kuchár')
  if (role === 'waiter') return tt('obsluha')
  if (role === 'driver') return tt('šofér')
  return role
}

export function refusalTitle(refusal: AccessRefusal): string {
  if (refusal.gate === 'super-admin') return tt('Táto stránka je len pre správcu platformy')
  if (refusal.gate === 'role') return tt('Táto stránka nie je pre vašu rolu')
  if (refusal.gate === 'franchise-module') return tt('Tento modul nie je v balíku tejto franšízy')
  return tt('Na túto stránku nemáte právo')
}

/** The guards' shape as far as rights go - a route's, a sidebar entry's or a section's. */
interface RightsGuard {
  permission?: string
  anyPermission?: readonly string[]
}

/**
 * The rights the app's own guards name - the catalogue, as far as the shell can see it.
 *
 * *Role a práva* titles a child right under its parent (`fiskal_settings` is *Nastavenia* under *Pokladňa &
 * uzávierky*), and finds the parent with `displayParentOf` over the catalogue it reads from
 * `/settings/permissions` - which answers the main administrator alone, so the shell cannot ask it. Every key here is
 * a catalogue key (`EveryRightAGuardAsksExistsTest`), so a parent found in this set is the parent the roles screen
 * finds; a parent no guard names is missed, and the right is then titled on its own, never under a wrong parent.
 */
export function rightsNamedBy(guards: Iterable<RightsGuard | null | undefined>): ReadonlySet<string> {
  const named = new Set<string>()
  for (const guard of guards) {
    if (guard === null || guard === undefined) continue
    if (typeof guard.permission === 'string' && guard.permission !== '') named.add(guard.permission)
    for (const key of guard.anyPermission ?? []) named.add(key)
  }
  return named
}

/**
 * A right as *Role a práva* titles it, with its key - §9.759.
 *
 * `permissionLabel` and `displayParentOf` are the roles screen's own, so there is no second label map to drift. A
 * child is titled as *Holders* titles one outside its group, parent and action; a right nobody has named keeps its
 * key alone (`translations_admin`, §9.145), rather than printing the key twice.
 */
export function rightTitle(key: string, known: ReadonlySet<string>): string {
  const parent = displayParentOf(key, known)
  const title =
    parent === null ? permissionLabel(key, null) : `${permissionLabel(parent, null)} - ${permissionLabel(key, parent)}`
  return title === key ? key : `${title} (${key})`
}

export function refusalDetail(
  refusal: AccessRefusal,
  role: string | undefined,
  known: ReadonlySet<string>,
): string {
  if (refusal.gate === 'role') {
    const roles = refusal.wanted.map((entry) => roleName(entry)).join(', ')
    // §9.759 - the third way into the driving app, and the one a cook or waiter who delivers can ask for.
    const admitted =
      refusal.orAlsoDrives === true
        ? `${roles} ${tt('alebo zamestnanec so zaškrtnutým „Jazdí aj rozvoz“')}`
        : roles
    const mine = role === undefined ? tt('neznáma rola') : roleName(role)
    return `${tt('Otvoriť ju môže')}: ${admitted}. ${tt('Vaša rola')}: ${mine}.`
  }
  if (refusal.gate === 'permission') {
    // Named, because "ask an administrator" is not actionable and the right is what they would grant - by the
    // title the roles screen shows, since §9.759.
    const wanted = refusal.wanted.map((key) => rightTitle(key, known)).join(tt(' alebo '))
    return `${tt('Chýba právo')}: ${wanted}. ${tt('Prideliť ho môže hlavný administrátor v Právach.')}`
  }
  if (refusal.gate === 'franchise-module') {
    return `${tt('Modul')}: ${refusal.wanted.join(', ')}. ${tt('O balíku rozhoduje centrála.')}`
  }
  return tt('Prihláste sa ako správca platformy.')
}
